import React, { useMemo, useState } from "react";
import { Box, Typography, Chip, Collapse, Button, CircularProgress } from "@mui/material";
import moment from "moment";
import {
  get_session_transcript_api, get_session_recording_api,
} from "../../../DAL/SchoolPortal/SchoolPortal";
import {
  border, textPrimary, textSecondary, textMuted, accent, font,
  success, warning, error as errorColor, easing,
} from "../../../layouts/schoolPortal/schoolPortalTheme";
import { card, label as labelSx, meta as metaSx, kindMeta, FILTERS, prettyName } from "./recordTheme";

/**
 * The record's centre column: one reverse-chronological stream of everything
 * that has happened to this student.
 *
 * WHY ONE STREAM AND NOT TABS
 * Sessions, notes, tasks, offers, written results, emails and failed payments
 * were previously separate lists in separate places, so nobody could see that
 * the note was written the day after the failed payment. Causation is the whole
 * value of a record, and it only shows in a single ordered list.
 *
 * Entries expand IN PLACE. Navigating away to read a transcript loses the
 * context you opened the record to get.
 */

const fmt = (d) => (d ? moment(d).format("D MMM YYYY") : "—");
const rel = (d) => (d ? moment(d).fromNow() : "");

function AcsChips({ codes = [], max = 6 }) {
  if (!codes.length) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
      {codes.slice(0, max).map((c) => (
        <Chip key={c} label={c} size="small" sx={{
          height: 18, fontSize: "10px", fontFamily: font, fontWeight: 700,
          backgroundColor: "rgba(37,99,235,0.08)", color: "#2563eb", borderRadius: "4px",
        }} />
      ))}
      {codes.length > max && (
        <Typography sx={{ ...metaSx, fontFamily: font, alignSelf: "center" }}>+{codes.length - max}</Typography>
      )}
    </Box>
  );
}

/** Full transcript, loaded only when someone opens it. */
function TranscriptDrill({ studentId, bookingId }) {
  const [state, setState] = useState({ loading: false, data: null, error: null, open: false });

  const load = async () => {
    if (state.data) return setState((s) => ({ ...s, open: !s.open }));
    setState((s) => ({ ...s, loading: true, open: true }));
    try {
      const r = await get_session_transcript_api(studentId, bookingId);
      if (r.code === 200) setState({ loading: false, data: r.transcript, error: null, open: true });
      else setState({ loading: false, data: null, error: r.message || "Could not load", open: true });
    } catch {
      setState({ loading: false, data: null, error: "Could not load the transcript", open: true });
    }
  };

  // Which segment produced which ACS tag, so a claim on the record is
  // checkable against the sentence that generated it rather than asserted.
  const evidenceFor = useMemo(() => {
    const m = new Map();
    (state.data?.acsEvidence || []).forEach((e) => {
      if (!m.has(e.segmentIndex)) m.set(e.segmentIndex, []);
      m.get(e.segmentIndex).push(e.code);
    });
    return m;
  }, [state.data]);

  return (
    <Box sx={{ mt: 1 }}>
      <Button onClick={load} size="small" sx={{
        textTransform: "none", fontFamily: font, fontSize: "11.5px", fontWeight: 600,
        color: accent, p: 0, minWidth: 0, "&:hover": { background: "none", textDecoration: "underline" },
      }}>
        {state.open ? "Hide transcript" : "Read transcript"}
      </Button>
      <Collapse in={state.open}>
        <Box sx={{ mt: 1, maxHeight: 320, overflowY: "auto", border: `1px solid ${border}`, borderRadius: "8px", p: 1.25 }}>
          {state.loading && <CircularProgress size={16} sx={{ color: accent }} />}
          {state.error && <Typography sx={{ ...metaSx, fontFamily: font, color: errorColor }}>{state.error}</Typography>}
          {state.data && (state.data.segments || []).map((seg, i) => (
            <Box key={i} sx={{ mb: 0.75 }}>
              <Typography sx={{ fontFamily: font, fontSize: "12px", color: textPrimary, lineHeight: 1.45 }}>
                <Box component="span" sx={{
                  fontWeight: 700,
                  color: seg.speakerRole === "instructor" ? accent : seg.speakerRole === "student" ? "#2563eb" : textMuted,
                }}>
                  {seg.speaker || seg.speakerRole}:{" "}
                </Box>
                {seg.text}
              </Typography>
              {evidenceFor.has(i) && <AcsChips codes={evidenceFor.get(i)} max={4} />}
            </Box>
          ))}
          {state.data && !(state.data.segments || []).length && (
            <Typography sx={{ ...metaSx, fontFamily: font }}>
              The transcript is {state.data.status === "pending" ? "still being processed" : "empty"}.
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

/** A signed, expiring playback URL, minted per click. */
function RecordingDrill({ studentId, bookingId }) {
  const [state, setState] = useState({ loading: false, url: null, error: null });
  const open = async () => {
    setState({ loading: true, url: null, error: null });
    try {
      const r = await get_session_recording_api(studentId, bookingId);
      if (r.code === 200 && r.url) setState({ loading: false, url: r.url, error: null });
      else setState({ loading: false, url: null, error: r.message || "No recording" });
    } catch {
      setState({ loading: false, url: null, error: "Could not open the recording" });
    }
  };
  if (state.url) {
    return (
      <Box sx={{ mt: 1 }}>
        <video src={state.url} controls style={{ width: "100%", maxHeight: 260, borderRadius: 8, background: "#000" }} />
        <Typography sx={{ ...metaSx, fontFamily: font, mt: 0.5 }}>This playback link expires in 15 minutes.</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ mt: 0.5 }}>
      <Button onClick={open} size="small" disabled={state.loading} sx={{
        textTransform: "none", fontFamily: font, fontSize: "11.5px", fontWeight: 600,
        color: accent, p: 0, minWidth: 0, "&:hover": { background: "none", textDecoration: "underline" },
      }}>
        {state.loading ? "Opening…" : "Play recording"}
      </Button>
      {state.error && <Typography sx={{ ...metaSx, fontFamily: font, color: errorColor, mt: 0.25 }}>{state.error}</Typography>}
    </Box>
  );
}

/** One row. Everything above the fold; detail on expand. */
function Entry({ e, studentId, onApproveSummary }) {
  const [open, setOpen] = useState(false);
  const km = kindMeta(e.kind);

  const headline = () => {
    switch (e.kind) {
      case "session":
        return `${prettyName(e.subject) || "Session"}${e.instructor ? ` with ${e.instructor}` : ""}`;
      case "summary":
        return e.approved === false ? "Session summary — awaiting your approval" : "Session summary";
      case "group_session":
        return `Group session — ${e.title || `Lesson ${e.lessonNumber || ""}`}`;
      case "session_offer":
        // The entry no competitor can show: why the platform offered help, and
        // — because suppressedReason is stored — why it sometimes did not.
        return e.outcome === "suppressed" ? "We did not offer a session" : "We offered a 1:1 session";
      case "written_result":
        return `FAA written — ${e.score}%${e.passed ? " (pass)" : " (fail)"}`;
      case "task":
        return e.title || "Task";
      case "note":
        return `Note${e.author ? ` by ${e.author}` : ""}`;
      case "message":
        // Named for what it was: something the student was actually sent.
        // Rendering it as "Note" meant an instructor could not tell what had
        // already been said, which is the difference between following up and
        // repeating yourself.
        return `Sent to the student${e.author ? ` by ${e.author}` : ""}`;
      case "email":
        return e.subject || "Email";
      case "payment_failed":
        return "Payment failed";
      default:
        return e.kind;
    }
  };

  const oneLine = () => {
    switch (e.kind) {
      case "session":
        return [
          e.status,
          e.minutes ? `${e.minutes} min` : null,
          e.noShow ? `no-show (${e.noShow})` : null,
        ].filter(Boolean).join(" · ");
      case "session_offer":
        return e.outcome === "suppressed" ? e.suppressedReason : `${e.signal} · student ${e.outcome}`;
      case "task":
        return [e.status, e.dueAt ? `due ${fmt(e.dueAt)}` : null, e.source === "assistant" && !e.approved ? "awaiting approval" : null]
          .filter(Boolean).join(" · ");
      case "email":
        // "sent" means handed to SMTP. There are no delivery events on this
        // path and the record must not imply otherwise.
        return `${e.emailType || e.category || "email"} · ${e.status === "sent" ? "sent" : e.status}`;
      case "payment_failed":
        return `${e.stage} — ${e.reason}`;
      case "note":
        return e.text?.length > 90 ? `${e.text.slice(0, 90)}…` : e.text;
      case "message":
        return e.text?.length > 90 ? `${e.text.slice(0, 90)}…` : e.text;
      case "summary":
        return e.text?.length > 90 ? `${e.text.slice(0, 90)}…` : e.text;
      default:
        return "";
    }
  };

  const expandable = ["session", "summary", "note", "message", "session_offer", "written_result", "task"].includes(e.kind);

  return (
    <Box sx={{ display: "flex", gap: 1.25, py: 1, borderBottom: `1px solid ${border}`, "&:last-child": { borderBottom: "none" } }}>
      <Box sx={{ width: 3, borderRadius: 2, backgroundColor: km.color, flexShrink: 0, mt: 0.4, mb: 0.4 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          onClick={() => expandable && setOpen((o) => !o)}
          sx={{ display: "flex", alignItems: "baseline", gap: 1, cursor: expandable ? "pointer" : "default" }}
        >
          <Typography sx={{ fontFamily: font, fontSize: "10.5px", fontWeight: 700, color: km.color, letterSpacing: "0.05em", textTransform: "uppercase", flexShrink: 0 }}>
            {km.label}
          </Typography>
          <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 600, color: textPrimary, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {headline()}
          </Typography>
          <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted, flexShrink: 0 }} title={fmt(e.at)}>
            {rel(e.at)}
          </Typography>
        </Box>

        {oneLine() && (
          <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, mt: 0.15, lineHeight: 1.4 }}>
            {oneLine()}
          </Typography>
        )}

        {e.kind === "session" && <AcsChips codes={e.acsCovered} />}
        {e.kind === "written_result" && <AcsChips codes={e.deficientCodes} />}
        {e.kind === "session_offer" && <AcsChips codes={e.acsCodes} />}

        <Collapse in={open} unmountOnExit>
          <Box sx={{ mt: 0.75 }}>
            {(e.kind === "note" || e.kind === "message" || e.kind === "summary") && (
              <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textPrimary, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                {e.text}
              </Typography>
            )}
            {/* Saved first, delivered second — the controller returns
                `delivered:false` when the mail throws and keeps the row. A
                message the student never received must not read as one they
                did. */}
            {e.kind === "message" && (
              <Typography sx={{ ...metaSx, fontFamily: font, mt: 0.5, color: e.deliveredAt ? textMuted : warning }}>
                {e.deliveredAt ? `Emailed ${rel(e.deliveredAt)}` : "Saved to the record — the email did not go"}
              </Typography>
            )}
            {e.kind === "summary" && e.approved === false && (
              <Button onClick={() => onApproveSummary(e.bookingId)} size="small" sx={{
                mt: 1, textTransform: "none", fontFamily: font, fontSize: "11.5px", fontWeight: 700,
                color: "#fff", backgroundColor: success, borderRadius: "999px", px: 1.75, py: 0.25,
                "&:hover": { backgroundColor: "#15803d" },
              }}>
                Approve and publish to student
              </Button>
            )}
            {e.kind === "session_offer" && (
              <Box>
                {e.offerText && <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textPrimary, lineHeight: 1.5 }}>{e.offerText}</Typography>}
                {e.evidenceNote && <Typography sx={{ ...metaSx, fontFamily: font, mt: 0.5 }}>Evidence: {e.evidenceNote}</Typography>}
              </Box>
            )}
            {e.kind === "written_result" && e.predictedReady !== null && e.predictedReady !== undefined && (
              <Typography sx={{ ...metaSx, fontFamily: font }}>
                We predicted this student was {e.predictedReady ? "ready" : "not ready"} before they sat it.
              </Typography>
            )}
            {e.kind === "task" && e.acsCodes?.length > 0 && <AcsChips codes={e.acsCodes} />}
            {e.kind === "session" && e.hasTranscript && <TranscriptDrill studentId={studentId} bookingId={e.bookingId} />}
            {e.kind === "session" && e.hasRecording && <RecordingDrill studentId={studentId} bookingId={e.bookingId} />}
            {e.kind === "session" && !e.hasTranscript && !e.hasRecording && (
              <Typography sx={{ ...metaSx, fontFamily: font }}>No recording or transcript was stored for this session.</Typography>
            )}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}

export default function RecordTimeline({ timeline = [], studentId, onApproveSummary }) {
  const [filter, setFilter] = useState("all");
  const [limit, setLimit] = useState(40);

  const counts = useMemo(() => {
    const c = {};
    timeline.forEach((e) => {
      const g = kindMeta(e.kind).group;
      c[g] = (c[g] || 0) + 1;
    });
    c.all = timeline.length;
    return c;
  }, [timeline]);

  /**
   * Session offers are the rate limiter talking about itself.
   *
   * A row reading "We offered a 1:1 session · instructor_flag · student
   * dismissed" is platform-operations telemetry rendered on a teaching page.
   * It tells an instructor that the machinery tried to sell their student
   * something and got refused, which is neither their business nor useful to
   * them, and on a brand-new student it was the ONLY thing in the history.
   *
   * Kept reachable under "Everything" rather than deleted, because when
   * someone is working out why a student went cold it is genuine evidence.
   */
  const teaching = useMemo(
    () => timeline.filter((e) => e.kind !== "session_offer"),
    [timeline]
  );

  const shown = useMemo(() => {
    const source = filter === "all" ? teaching : timeline;
    const list = filter === "all" ? source : source.filter((e) => kindMeta(e.kind).group === filter);
    return list.slice(0, limit);
  }, [timeline, teaching, filter, limit]);

  const total = filter === "all" ? timeline.length : counts[filter] || 0;

  return (
    <Box sx={{ ...card, p: 0, overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.75, pt: 1.5, pb: 1, flexWrap: "wrap", borderBottom: `1px solid ${border}` }}>
        <Typography sx={{ ...labelSx, fontFamily: font, mr: 0.5 }}>History</Typography>
        {FILTERS.filter((f) => f.key === "all" || counts[f.key]).map((f) => (
          <Box
            key={f.key}
            onClick={() => { setFilter(f.key); setLimit(40); }}
            sx={{
              px: 1, py: 0.25, borderRadius: "999px", cursor: "pointer", fontFamily: font,
              fontSize: "11.5px", fontWeight: 600, transition: `all 0.15s ${easing}`,
              color: filter === f.key ? "#fff" : textSecondary,
              backgroundColor: filter === f.key ? textPrimary : "transparent",
              border: `1px solid ${filter === f.key ? textPrimary : border}`,
            }}
          >
            {f.label} {counts[f.key] ? `(${counts[f.key]})` : ""}
          </Box>
        ))}
      </Box>

      <Box sx={{ px: 1.75, py: 0.5 }}>
        {shown.map((e, i) => (
          <Entry key={`${e.kind}-${e.at || i}-${i}`} e={e} studentId={studentId} onApproveSummary={onApproveSummary} />
        ))}
        {!shown.length && (
          // Honest empty state: says what fills it, not "no data yet".
          <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary, py: 2 }}>
            Nothing here yet. This fills as sessions are taught, notes are written and the platform offers help.
          </Typography>
        )}
      </Box>

      {total > shown.length && (
        <Box sx={{ px: 1.75, pb: 1.25 }}>
          <Button onClick={() => setLimit((l) => l + 40)} size="small" sx={{
            textTransform: "none", fontFamily: font, fontSize: "12px", fontWeight: 600, color: accent, p: 0,
          }}>
            Show {Math.min(40, total - shown.length)} more of {total}
          </Button>
        </Box>
      )}
    </Box>
  );
}
